
import React, { useState, useEffect } from "react";

import {
  Box,
  Card,
  Grid,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Stack,
  Chip,
  LinearProgress,
  Avatar,
} from "@mui/material";

import {
  Business,
  Hotel,
  Phone,
  Restaurant,
  RestaurantMenu,
  FreeBreakfast,
  LunchDining,
  Coffee,
  DinnerDining,
  AccessTime,
  Bed,
  Groups,
  PersonOff,
  CalendarMonth,
  EventAvailable,
  SupportAgent,
  Visibility,
  MeetingRoom,
  NoMeals,
  TrendingUp,
  EventBusy,
} from "@mui/icons-material";

import HeaderMenu from "../Header/HeaderMenu";
import axiosApi from "../Utils/axiosApi";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const HostelOperatorDashboard = () => {
  const [operatorData, setOperatorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ==========================================
  // FETCH DASHBOARD DATA
  // ==========================================

  const fetchDashboardData = async () => {
    try {
      setError(null);

      const today = new Date();

      const localCurrentDate =
        today.getFullYear() +
        "-" +
        String(today.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(today.getDate()).padStart(2, "0");

      const res = await axiosApi.post(
        "/dashBoard/getHostelOperatorSummaryDashboard",
        {
          currentDate: localCurrentDate,
        }
      );

      const dto = res.data ?? {};

      const mapped = [
        {
          operatorName:
            dto.operatorCompanyName ?? "Unknown Operator",

          hostels: Array.isArray(dto.hostelDashBoardDTOs)
            ? dto.hostelDashBoardDTOs.map((h) => ({
                id: h.hostelId ?? 0,
                name: h.hostelName ?? "Unnamed Hostel",
                mobile: h.mobile ?? "--",

                hostelClosure: h.hostelClosure === true,

                presentTodayMessCount:
                  h.presentTodayMessCount ?? 0,

                capacity: h.totalCapacity ?? 0,
                occupied: h.occupied ?? 0,

                messCountTotal: h.messCountTotal ?? 0,

                foodOnlyHostelers:
                  h.foodOnlyHostelers ?? 0,

                stayOnlyHostelers:
                  h.stayOnlyHostelers ?? 0,

                stayWithFoodHostelers:
                  h.stayWithFoodHostelers ?? 0,

                availableVacancy:
                  h.availableVacancy ?? 0,

                callbacks: h.callbacks ?? 0,
                visits: h.visits ?? 0,

                todayLeaveCount:
                  h.todayLeaveCount ?? 0,

                todayAbsentList: Array.isArray(
                  h.todayHostelerAbsentList
                )
                  ? h.todayHostelerAbsentList
                  : [],

                todayMenu: {
                  breakfast: h.todayBreakfastMenu || "N/A",
                  lunch: h.todayLunchMenu || "N/A",
                  snacks: h.todaySnacksMenu || "N/A",
                  dinner: h.todayDinnerMenu || "N/A",
                },

                timings: {
                  breakfast: h.todayBreakfastTime || "--",
                  lunch: h.todayLunchTime || "--",
                  snacks: h.todaySnacksTime || "--",
                  dinner: h.todayDinnerTime || "--",
                },

                monthlyBookingCount:
                  h.monthlyBookingCount ?? 0,

                todayAddOns: Array.isArray(
                  h.hostelAddOnMenuItemDTOs
                )
                  ? h.hostelAddOnMenuItemDTOs
                  : [],
              }))
            : [],
        },
      ];

      setOperatorData(mapped);
    } catch (e) {
      console.error("Dashboard API Error:", e);
      setError("Unable to load dashboard. Please try again.");
    }
  };

  // ==========================================
  // OVERALL CALCULATIONS
  // ==========================================

  const allHostels = operatorData.flatMap(
    (operator) => operator.hostels || []
  );

  const totalMessAttendance = allHostels.reduce(
    (sum, h) =>
      sum + Number(h.presentTodayMessCount || 0),
    0
  );

  const totalAbsent = allHostels.reduce(
    (sum, h) =>
      sum + (h.todayAbsentList?.length || 0),
    0
  );

  const totalCapacity = allHostels.reduce(
    (sum, h) => sum + Number(h.capacity || 0),
    0
  );

  const totalOccupied = allHostels.reduce(
    (sum, h) => sum + Number(h.occupied || 0),
    0
  );

  const totalVacancy = allHostels.reduce(
    (sum, h) => sum + Number(h.availableVacancy || 0),
    0
  );

 

  // ==========================================
  // LOAD
  // ==========================================

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      await fetchDashboardData();
      setLoading(false);
    };

    loadDashboard();
  }, []);

  // ==========================================
  // COMMON STYLES
  // ==========================================

  const sectionCard = {
    p: { xs: 2, md: 2.5 },
    borderRadius: 3,
    border: "1px solid #e5eaf1",
    boxShadow: "0 2px 10px rgba(15,23,42,0.035)",
    backgroundColor: "#ffffff",
  };

  // ==========================================
  // LOADING
  // ==========================================

  if (loading) {
    return (
      <>
        <HeaderMenu />

        <Box
          minHeight="75vh"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <CircularProgress size={45} />

          <Typography color="text.secondary">
            Loading operator dashboard...
          </Typography>
        </Box>
      </>
    );
  }

  // ==========================================
  // ERROR
  // ==========================================

  if (error) {
    return (
      <>
        <HeaderMenu />

        <Box p={4} textAlign="center">
          <Typography color="error" fontWeight={700}>
            {error}
          </Typography>
        </Box>
      </>
    );
  }

  // ==========================================
  // EMPTY STATE
  // ==========================================

  if (!allHostels.length) {
    return (
      <>
        <HeaderMenu />

        <Box
          minHeight="70vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap={2}
        >
          <Hotel
            sx={{
              fontSize: 65,
              color: "#94a3b8",
            }}
          />

          <Typography variant="h6" fontWeight={800}>
            No Hostel Data Available
          </Typography>

          <Typography color="text.secondary">
            No property information is currently available.
          </Typography>
        </Box>
      </>
    );
  }

  // ==========================================
  // SECTION HEADER
  // ==========================================

  const SectionHeader = ({
    icon,
    title,
    subtitle,
    action,
  }) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1}
      mb={2}
    >
      <Stack direction="row" alignItems="center" spacing={1.2}>
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: "#eff6ff",
            color: "#2563eb",
          }}
        >
          {icon}
        </Avatar>

        <Box>
          <Typography fontWeight={800} fontSize={16}>
            {title}
          </Typography>

          {subtitle && (
            <Typography
              fontSize={11}
              color="text.secondary"
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {action}
    </Stack>
  );

  // ==========================================
  // OVERALL SUMMARY CARD
  // ==========================================

  const SummaryCard = ({
    title,
    value,
    subtitle,
    icon,
    color,
    background,
  }) => (
    <Card
      elevation={0}
      sx={{
        p: 2.2,
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e5eaf1",
        backgroundColor: "#ffffff",
        transition: "0.2s",

        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 6px 18px rgba(15,23,42,0.07)",
        },
      }}
    >
      <Stack spacing={1.3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            fontSize={12}
            fontWeight={700}
            color="text.secondary"
          >
            {title}
          </Typography>

          <Avatar
            variant="rounded"
            sx={{
              width: 40,
              height: 40,
              bgcolor: background,
              color,
            }}
          >
            {icon}
          </Avatar>
        </Stack>

        <Typography
          variant="h4"
          fontWeight={900}
          color="#172033"
        >
          {value}
        </Typography>

        <Typography
          fontSize={11}
          color="text.secondary"
        >
          {subtitle}
        </Typography>
      </Stack>
    </Card>
  );

  // ==========================================
  // MEAL CARD
  // ==========================================

  const MealCard = ({ meal }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2.5,
        borderColor: "#e5eaf1",
        backgroundColor: "#fafbfe",
        transition: "0.2s",

        "&:hover": {
          borderColor: "#93c5fd",
          backgroundColor: "#f5f9ff",
        },
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1.3}>
          <Avatar
            variant="rounded"
            sx={{
              width: 40,
              height: 40,
              bgcolor: "#e8f0fe",
              color: "#2563eb",
            }}
          >
            {meal.icon}
          </Avatar>

          <Typography fontWeight={800} fontSize={14}>
            {meal.title}
          </Typography>
        </Stack>

        <Box>
          <Typography
            fontSize={10}
            fontWeight={800}
            color="text.secondary"
            mb={0.6}
          >
            MENU DETAILS
          </Typography>

          <Typography
            fontSize={13}
            fontWeight={600}
            color="#334155"
            sx={{
              lineHeight: 1.7,
              minHeight: 44,
              overflowWrap: "anywhere",
            }}
          >
            {meal.menu || "N/A"}
          </Typography>
        </Box>

        <Divider />

        <Stack direction="row" spacing={0.8} alignItems="center">
          <AccessTime
            sx={{
              fontSize: 16,
              color: "#64748b",
            }}
          />

          <Typography
            fontSize={10}
            fontWeight={800}
            color="text.secondary"
          >
            SERVING TIME
          </Typography>
        </Stack>

        <Typography
          fontSize={13}
          fontWeight={800}
          color="#2563eb"
        >
          {meal.time || "--"}
        </Typography>
      </Stack>
    </Paper>
  );

  // ==========================================
  // TODAY MENU SECTION
  // ==========================================

  const TodayMenuSection = ({ hostel }) => {
    const meals = [
      {
        title: "Breakfast",
        menu: hostel.todayMenu.breakfast,
        time: hostel.timings.breakfast,
        icon: <FreeBreakfast />,
      },
      {
        title: "Lunch",
        menu: hostel.todayMenu.lunch,
        time: hostel.timings.lunch,
        icon: <LunchDining />,
      },
      {
        title: "Snacks",
        menu: hostel.todayMenu.snacks,
        time: hostel.timings.snacks,
        icon: <Coffee />,
      },
      {
        title: "Dinner",
        menu: hostel.todayMenu.dinner,
        time: hostel.timings.dinner,
        icon: <DinnerDining />,
      },
    ];

    return (
      <Card elevation={0} sx={sectionCard}>
        <SectionHeader
          icon={<RestaurantMenu />}
          title="Today's Food Menu"
          subtitle="Meal details and daily serving schedule"
          action={
            <Chip
              size="small"
              label="Daily Menu"
              color="primary"
              variant="outlined"
            />
          }
        />

        <Divider sx={{ mb: 2.5 }} />

        <Grid container spacing={2}>
          {meals.map((meal) => (
            <Grid item xs={12} sm={6} lg={3} key={meal.title}>
              <MealCard meal={meal} />
            </Grid>
          ))}
        </Grid>
      </Card>
    );
  };

  // ==========================================
  // ABSENT SECTION
  // ==========================================

  const AbsentSection = ({ hostel }) => (
    <Card
      elevation={0}
      sx={{
        ...sectionCard,
        height: "100%",
      }}
    >
      <SectionHeader
        icon={<PersonOff />}
        title="Absent Today"
        subtitle="Hostelers marked absent"
        action={
          <Chip
            size="small"
            label={`${hostel.todayAbsentList.length} Absent`}
            color={
              hostel.todayAbsentList.length > 0
                ? "error"
                : "success"
            }
          />
        }
      />

      <Divider sx={{ mb: 2 }} />

      <Typography
        variant="h3"
        fontWeight={900}
        color={
          hostel.todayAbsentList.length > 0
            ? "#dc2626"
            : "#16a34a"
        }
      >
        {hostel.todayAbsentList.length}
      </Typography>

      <Typography
        fontSize={12}
        color="text.secondary"
        mb={2}
      >
        Total absent hostelers today
      </Typography>

      <Divider sx={{ mb: 1.5 }} />

      <Typography
        fontSize={11}
        fontWeight={800}
        color="text.secondary"
        mb={1}
      >
        ABSENT HOSTELER DETAILS
      </Typography>

      {hostel.todayAbsentList.length > 0 ? (
        <Stack spacing={1}>
          {hostel.todayAbsentList.map((name, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: 1.2,
                borderRadius: 2,
                borderColor: "#fee2e2",
                bgcolor: "#fffafa",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Avatar
                  sx={{
                    width: 29,
                    height: 29,
                    fontSize: 12,
                    bgcolor: "#fee2e2",
                    color: "#dc2626",
                  }}
                >
                  {String(name).charAt(0).toUpperCase()}
                </Avatar>

                <Typography fontSize={13} fontWeight={600}>
                  {name}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Typography
          textAlign="center"
          color="success.main"
          fontWeight={700}
          fontSize={13}
          py={2}
        >
          No absentees today
        </Typography>
      )}
    </Card>
  );

  // ==========================================
  // MONTHLY SUMMARY SECTION
  // ==========================================

  const MonthlySummary = ({ hostel }) => (
    <Card
      elevation={0}
      sx={{
        ...sectionCard,
        height: "100%",
      }}
    >
      <SectionHeader
        icon={<CalendarMonth />}
        title="Monthly Summary"
        subtitle="Monthly business activity"
      />

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        {[
          {
            title: "Callbacks",
            value: hostel.callbacks,
            icon: <SupportAgent />,
            color: "#2563eb",
            background: "#eff6ff",
          },
          {
            title: "Visits",
            value: hostel.visits,
            icon: <Visibility />,
            color: "#7c3aed",
            background: "#f5f3ff",
          },
          {
            title: "Monthly Bookings",
            value: hostel.monthlyBookingCount,
            icon: <EventAvailable />,
            color: "#059669",
            background: "#ecfdf5",
          },
        ].map((item) => (
          <Paper
            key={item.title}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: "#e5eaf1",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.2}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: item.background,
                    color: item.color,
                  }}
                >
                  {item.icon}
                </Avatar>

                <Typography
                  fontSize={13}
                  fontWeight={700}
                  color="text.secondary"
                >
                  {item.title}
                </Typography>
              </Stack>

              <Typography
                variant="h5"
                fontWeight={900}
                color="#172033"
              >
                {item.value}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Card>
  );

  // ==========================================
  // ADD-ON ITEMS
  // ==========================================

  const AddOnSection = ({ hostel }) => {
    if (!hostel.todayAddOns.length) return null;

    return (
      <Card
        elevation={0}
        sx={{
          ...sectionCard,
          mt: 3,
          background:
            "linear-gradient(135deg, #fffaf0 0%, #ffffff 100%)",
          borderColor: "#fed7aa",
        }}
      >
        <SectionHeader
          icon={<NoMeals />}
          title="Today's Add-On Items"
          subtitle="Extra items not included in the regular menu"
          action={
            <Chip
              size="small"
              label={`${hostel.todayAddOns.length} Items`}
              color="warning"
              variant="outlined"
            />
          }
        />

        <Divider sx={{ mb: 2 }} />

        <Typography
          fontSize={12}
          color="text.secondary"
          mb={2}
        >
          Extra items · Not included in today's menu · Charged separately
        </Typography>

        <Grid container spacing={1.5}>
          {hostel.todayAddOns.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: "#fed7aa",
                  backgroundColor: "#ffffff",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Restaurant
                      sx={{
                        color: "#ea580c",
                        fontSize: 19,
                      }}
                    />

                    <Typography
                      fontSize={13}
                      fontWeight={700}
                    >
                      {item.itemName}
                    </Typography>
                  </Stack>

                  <Chip
                    size="small"
                    label={`₹${item.itemPrice ?? 0}`}
                    color="success"
                    variant="outlined"
                  />
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Card>
    );
  };

  // ==========================================
  // HOSTEL MAIN DASHBOARD
  // ==========================================

  return (
    <>
      <HeaderMenu />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f4f6fa",
          p: {
            xs: 1.5,
            sm: 2,
            md: 3,
          },
        }}
      >
        {/* PAGE HEADER */}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 3,
            borderRadius: 3,
            border: "1px solid #e5eaf1",
            bgcolor: "#ffffff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Avatar
                  sx={{
                    bgcolor: "#eff6ff",
                    color: "#2563eb",
                  }}
                >
                  <Business />
                </Avatar>

                <Typography
                  fontWeight={900}
                  sx={{
                    fontSize: {
                      xs: 22,
                      md: 28,
                    },
                    color: "#172033",
                  }}
                >
                  Hostel Operator Dashboard
                </Typography>
              </Stack>

              <Typography
                color="text.secondary"
                fontSize={13}
                mt={1}
              >
                Manage your properties, occupancy, mess attendance
                and daily operations in one place.
              </Typography>
            </Box>

            <Chip
              icon={<EventAvailable />}
              label="Today's Overview"
              color="primary"
              variant="outlined"
            />
          </Stack>
        </Paper>

        {/* OPERATOR OVERALL SUMMARY */}

        <Box mb={3}>
          <SectionHeader
            icon={<TrendingUp />}
            title="Overall Business Overview"
            subtitle="Combined summary across all your properties"
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="Total Mess Attendance"
                value={totalMessAttendance}
                subtitle="Present in today's mess"
                icon={<Restaurant />}
                color="#059669"
                background="#ecfdf5"
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="Total Absent Today"
                value={totalAbsent}
                subtitle="Absent hostelers"
                icon={<PersonOff />}
                color="#dc2626"
                background="#fef2f2"
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="Total Occupancy"
                value={totalOccupied}
                subtitle={`Total capacity: ${totalCapacity}`}
                icon={<Hotel />}
                color="#2563eb"
                background="#eff6ff"
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="Available Vacancy"
                value={totalVacancy}
                subtitle="Available beds"
                icon={<Bed />}
                color="#d97706"
                background="#fffbeb"
              />
            </Grid>
          </Grid>
        </Box>

        {/* OPERATOR BUSINESS INFORMATION */}

        {operatorData.map((op, operatorIndex) => (
          <Box
            key={`${op.operatorName}-${operatorIndex}`}
            mb={4}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 3,
                border: "1px solid #e5eaf1",
                bgcolor: "#ffffff",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
              >
                <Avatar
                  sx={{
                    width: 46,
                    height: 46,
                    bgcolor: "#eff6ff",
                    color: "#2563eb",
                  }}
                >
                  <Business />
                </Avatar>

                <Box>
                  <Typography
                    fontSize={11}
                    fontWeight={800}
                    color="text.secondary"
                  >
                    PROPERTY OPERATOR
                  </Typography>

                  <Typography
                    variant="h6"
                    fontWeight={900}
                    color="#172033"
                  >
                    {op.operatorName}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* HOSTEL-WISE DASHBOARD */}

            {op.hostels.map((h) => {
              const present =
                Number(h.presentTodayMessCount) || 0;

              const total =
                Number(h.messCountTotal) || 0;

              const eatingPercent = total
                ? Math.min((present / total) * 100, 100)
                : 0;

              const occupancyPercent = h.capacity
                ? Math.min(
                    (h.occupied / h.capacity) * 100,
                    100
                  )
                : 0;

              // Original chart attributes preserved

              const capacityChartData = [
                {
                  category: "Beds",
                  Capacity: h.capacity,
                  Occupied: h.occupied,
                  Vacancy: h.availableVacancy,
                },
                {
                  category: "Mess",
                  "Total Consumers": h.messCountTotal,
                  "Food Only": h.foodOnlyHostelers,
                  "Stay + Food": h.stayWithFoodHostelers,
                },
                {
                  category: "Stay Only",
                  "Stay Only": h.stayOnlyHostelers,
                },
              ];

              return (
                <Card
                  key={h.id}
                  elevation={0}
                  sx={{
                    p: {
                      xs: 1.5,
                      sm: 2,
                      md: 2.5,
                    },
                    mb: 3,
                    borderRadius: 3,
                    border: "1px solid #e5eaf1",
                    bgcolor: "#ffffff",
                    boxShadow:
                      "0 3px 14px rgba(15,23,42,0.035)",
                  }}
                >
                  {/* HOSTEL HEADER */}

                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    justifyContent="space-between"
                    alignItems={{
                      xs: "flex-start",
                      sm: "center",
                    }}
                    spacing={1.5}
                    mb={2.5}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: "#eff6ff",
                          color: "#2563eb",
                        }}
                      >
                        <Hotel />
                      </Avatar>

                      <Box>
                        <Typography
                          fontSize={11}
                          fontWeight={800}
                          color="text.secondary"
                        >
                          PROPERTY
                        </Typography>

                        <Typography
                          variant="h6"
                          fontWeight={900}
                          color="#172033"
                        >
                          {h.name}
                        </Typography>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Phone
                            sx={{
                              fontSize: 14,
                              color: "text.secondary",
                            }}
                          />

                          <Typography
                            fontSize={12}
                            color="text.secondary"
                          >
                            {h.mobile}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>

                    <Chip
                      label={
                        h.hostelClosure
                          ? "Hostel Closed Today"
                          : "Operational"
                      }
                      color={
                        h.hostelClosure
                          ? "warning"
                          : "success"
                      }
                      variant="outlined"
                      size="small"
                    />
                  </Stack>

                  {/* HOSTEL CLOSED NOTICE */}

                  {h.hostelClosure && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: 2.5,
                        borderRadius: 2,
                        border: "1px solid #fed7aa",
                        bgcolor: "#fff7ed",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <EventBusy
                          sx={{ color: "#ea580c" }}
                        />

                        <Box>
                          <Typography
                            fontWeight={800}
                            color="#c2410c"
                            fontSize={13}
                          >
                            Hostel Closed Today
                          </Typography>

                          <Typography
                            fontSize={12}
                            color="text.secondary"
                          >
                            No meals are served today due to
                            hostel closure.
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                  {/* HOSTEL OCCUPANCY SUMMARY */}

                  <SectionHeader
                    icon={<Hotel />}
                    title="Property Overview"
                    subtitle="Capacity and occupancy information"
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <SummaryCard
                        title="Occupied"
                        value={h.occupied}
                        subtitle={`${occupancyPercent.toFixed(1)}% occupancy`}
                        icon={<Groups />}
                        color="#2563eb"
                        background="#eff6ff"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <SummaryCard
                        title="Available Vacancy"
                        value={h.availableVacancy}
                        subtitle="Available beds"
                        icon={<Bed />}
                        color="#059669"
                        background="#ecfdf5"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <SummaryCard
                        title="Total Capacity"
                        value={h.capacity}
                        subtitle="Total property capacity"
                        icon={<MeetingRoom />}
                        color="#d97706"
                        background="#fffbeb"
                      />
                    </Grid>
                  </Grid>

                  {/* OCCUPANCY PROGRESS */}

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mt: 2,
                      mb: 3,
                      borderRadius: 2.5,
                      border: "1px solid #e5eaf1",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography
                        fontSize={13}
                        fontWeight={800}
                      >
                        Occupancy Rate
                      </Typography>

                      <Typography
                        fontSize={13}
                        fontWeight={800}
                        color="primary.main"
                      >
                        {occupancyPercent.toFixed(1)}%
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={occupancyPercent}
                      sx={{
                        height: 9,
                        borderRadius: 5,
                        bgcolor: "#e5e7eb",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 5,
                          bgcolor: "#2563eb",
                        },
                      }}
                    />
                  </Paper>

                  {/* MESS ATTENDANCE + ABSENT + MONTHLY */}

                  <SectionHeader
                    icon={<Restaurant />}
                    title="Daily Operations"
                    subtitle="Mess attendance, absentee details and business activity"
                  />

                  <Grid container spacing={2}>
                    {/* MESS ATTENDANCE */}

                    <Grid item xs={12} md={4}>
                      <Card
                        elevation={0}
                        sx={{
                          ...sectionCard,
                          height: "100%",
                          bgcolor: "#ffffff",
                        }}
                      >
                        <SectionHeader
                          icon={<Restaurant />}
                          title="Mess Attendance Today"
                          subtitle="Today's food consumption"
                        />

                        <Typography
                          variant="h3"
                          fontWeight={900}
                          color="#059669"
                        >
                          {present}
                        </Typography>

                        <Typography
                          fontSize={12}
                          color="text.secondary"
                          mb={1.5}
                        >
                          Present in today's mess
                        </Typography>

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          mb={0.8}
                        >
                          <Typography
                            fontSize={12}
                            color="text.secondary"
                          >
                            Consumption
                          </Typography>

                          <Typography
                            fontSize={12}
                            fontWeight={800}
                            color="success.main"
                          >
                            {eatingPercent.toFixed(1)}%
                          </Typography>
                        </Stack>

                        <LinearProgress
                          variant="determinate"
                          value={eatingPercent}
                          sx={{
                            height: 8,
                            borderRadius: 5,
                            bgcolor: "#e5e7eb",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: "#16a34a",
                              borderRadius: 5,
                            },
                          }}
                        />

                        <Divider sx={{ my: 2 }} />

                        <Stack spacing={1.5}>
                          {[
                            {
                              label: "Total Mess Consumers",
                              value: h.messCountTotal,
                            },
                            {
                              label: "Food Only",
                              value: h.foodOnlyHostelers,
                            },
                            {
                              label: "Stay + Food",
                              value: h.stayWithFoodHostelers,
                            },
                            {
                              label: "Stay Only",
                              value: h.stayOnlyHostelers,
                            },
                          ].map((item) => (
                            <Stack
                              key={item.label}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography
                                fontSize={12}
                                color="text.secondary"
                              >
                                {item.label}
                              </Typography>

                              <Typography
                                fontSize={13}
                                fontWeight={800}
                              >
                                {item.value}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Card>
                    </Grid>

                    {/* ABSENT TODAY */}

                    <Grid item xs={12} md={4}>
                      <AbsentSection hostel={h} />
                    </Grid>

                    {/* MONTHLY SUMMARY */}

                    <Grid item xs={12} md={4}>
                      <MonthlySummary hostel={h} />
                    </Grid>
                  </Grid>

                  {/* LEAVE SUMMARY */}

                  <Card
                    elevation={0}
                    sx={{
                      ...sectionCard,
                      mt: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.2}
                      >
                        <Avatar
                          variant="rounded"
                          sx={{
                            bgcolor: "#fff7ed",
                            color: "#ea580c",
                            width: 38,
                            height: 38,
                          }}
                        >
                          <EventBusy />
                        </Avatar>

                        <Box>
                          <Typography
                            fontWeight={800}
                            fontSize={14}
                          >
                            Today's Leave Summary
                          </Typography>

                          <Typography
                            fontSize={11}
                            color="text.secondary"
                          >
                            Hostelers on leave today
                          </Typography>
                        </Box>
                      </Stack>

                      <Typography
                        variant="h5"
                        fontWeight={900}
                        color="#ea580c"
                      >
                        {h.todayLeaveCount}
                      </Typography>
                    </Stack>
                  </Card>

                  {/* TODAY MENU */}

                  <Box mt={3}>
                    <TodayMenuSection hostel={h} />
                  </Box>

                  {/* TODAY ADD-ON ITEMS */}

                  <AddOnSection hostel={h} />

                  {/* CAPACITY AND MESS CHART */}

                  <Card
                    elevation={0}
                    sx={{
                      ...sectionCard,
                      mt: 3,
                    }}
                  >
                    <SectionHeader
                      icon={<TrendingUp />}
                      title="Hostel Capacity & Mess Overview"
                      subtitle="Beds, occupancy, vacancy and mess consumption breakdown"
                    />

                    <Divider sx={{ mb: 2 }} />

                    <Box
                      width="100%"
                      height={{
                        xs: 340,
                        sm: 360,
                      }}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <BarChart
                          data={capacityChartData}
                          margin={{
                            top: 15,
                            right: 15,
                            left: 0,
                            bottom: 10,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e5e7eb"
                          />

                          <XAxis
                            dataKey="category"
                            tick={{
                              fontSize: 12,
                              fill: "#64748b",
                            }}
                          />

                          <YAxis
                            allowDecimals={false}
                            tick={{
                              fontSize: 11,
                              fill: "#64748b",
                            }}
                          />

                          <Tooltip
                            contentStyle={{
                              borderRadius: 10,
                              border: "1px solid #e5e7eb",
                              fontSize: 12,
                            }}
                          />

                          <Legend
                            wrapperStyle={{
                              fontSize: 11,
                              paddingTop: 10,
                            }}
                          />

                          {/* BEDS */}

                          <Bar
                            dataKey="Capacity"
                            fill="#90caf9"
                            radius={[4, 4, 0, 0]}
                          />

                          <Bar
                            dataKey="Occupied"
                            fill="#66bb6a"
                            radius={[4, 4, 0, 0]}
                          />

                          <Bar
                            dataKey="Vacancy"
                            fill="#ef5350"
                            radius={[4, 4, 0, 0]}
                          />

                          {/* MESS */}

                          <Bar
                            dataKey="Total Consumers"
                            fill="#ffb74d"
                            radius={[4, 4, 0, 0]}
                          />

                          <Bar
                            dataKey="Food Only"
                            fill="#42a5f5"
                            radius={[4, 4, 0, 0]}
                          />

                          <Bar
                            dataKey="Stay + Food"
                            fill="#81c784"
                            radius={[4, 4, 0, 0]}
                          />

                          {/* STAY */}

                          <Bar
                            dataKey="Stay Only"
                            fill="#ce93d8"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Card>
                </Card>
              );
            })}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default HostelOperatorDashboard;
